import React from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";
import { RATE_LIMITED } from "../../../utilities/apiUtils";

// An insured can have hundreds of additional interests on the selected certificate's
// policies, so the picker shows one page at a time with the same prev/next footer the
// Lines of Business picker uses (src/features/policies/LobSearchDropdown.tsx), and the
// same 20-row page size, which is what AMS serves per response. Selection lives in
// `additionalInterest` and is independent of what is on screen, so it survives paging.
//
// Unlike that picker this one pages in the browser rather than at the server: the options
// are whatever survives the selected master certificate's policy filter, and that filter
// has no server-side equivalent — PolicyAdditionalInterestDetailList exposes `policies`
// as a plain complex collection, so AMS cannot be asked for "interests on these policies".
const AI_PAGE_SIZE = 20;

export type GenrateCertificateProps = {
  visible: boolean;
  onHide: () => void;
  insuredDatabaseId?: string;
};

// An insured can have several hundred additional interests (Farmers tracking #268: 398),
// so a single top=100 request silently drops the rest and the policy filter below can
// end up with zero matches. Page through until the server's reported total is exhausted.
//
// How many rows come back is the server's decision, not ours, so neither a short page nor
// a full one is on its own proof of anything:
//
//   - A page shorter than the requested top is the end only when the server is not saying
//     otherwise. AMS serves its own page size when that is smaller than our $top and
//     advertises the remainder through @odata.nextLink, so a short page carrying a
//     nextLink is a truncated page rather than the last one. That is what limited this
//     dropdown to 20 entries while the AMS page size was 20.
//   - A full page is never proof of the end either: asking for exactly $top rows returns
//     $top rows with no nextLink even when more remain.
//
// So we advance by the rows actually received and stop only on an empty page, on reaching
// the server's reported total, or on a short page the server has not flagged as truncated.
// The nextLink itself is an absolute AMS URL that cannot be called from the browser, so it
// is read purely as a "there is more" signal while we keep paging with our own $skip.
export async function fetchAllAdditionalInterests(
  baseUrl: string,
  insuredDatabaseId: string,
  headers: Record<string, string>,
  pageSize: number = 100
): Promise<any[]> {
  const all: any[] = [];
  let received = 0; // rows the server has handed us, counted before de-duplication
  const maxPages = 100; // hard stop so a bad server total can never loop forever

  for (let page = 0; page < maxPages; page++) {
    const res = await fetch(
      `${baseUrl}/api/IGIGCOI/policy-additional-interests?insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}&top=${pageSize}&skip=${received}`,
      { headers }
    );
    if (!res.ok) throw res;
    const data = await res.json();
    const items = Array.isArray(data.value) ? data.value : [];

    all.push(...items);
    received += items.length;

    if (items.length === 0) break;

    const total = data['@odata.count'];
    if (typeof total === 'number' && received >= total) break;

    if (items.length < pageSize && !data['@odata.nextLink']) break;
  }

  // Page boundaries are only as stable as the server's ordering, so the same record can
  // surface on two pages. Keep the first occurrence of each databaseId.
  const seen = new Set<string>();
  return all.filter((item: any) => {
    if (seen.has(item.databaseId)) return false;
    seen.add(item.databaseId);
    return true;
  });
}

const GenrateCertificate: React.FC<GenrateCertificateProps> = ({ visible, onHide, insuredDatabaseId }) => {
  const toastRef = React.useRef<any>(null);
  const [certificate, setCertificate] = React.useState<string | null>(null);
  const [masterCertificateOptions, setMasterCertificateOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [masterCertificatesRaw, setMasterCertificatesRaw] = React.useState<any[]>([]);
  const [loadingMaster, setLoadingMaster] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [additionalInterest, setAdditionalInterest] = React.useState<string[]>([]);
  const [additionalInterestOptions, setAdditionalInterestOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [additionalInterestsRaw, setAdditionalInterestsRaw] = React.useState<any[]>([]);
  const [additionalInterestFirst, setAdditionalInterestFirst] = React.useState(0);
  const [additionalInterestSearch, setAdditionalInterestSearch] = React.useState('');
  const [loadingInterest, setLoadingInterest] = React.useState(true);
  const [interestError, setInterestError] = React.useState<string | null>(null);
  const [downloadOption, setDownloadOption] = React.useState<string>('pdf');
  const downloadOptions = [
    { label: "All certificates in a single .pdf", value: "pdf" },
    { label: "All certificates in a .zip", value: "zip" },
  ];
  const [saveOption, setSaveOption] = React.useState<string | null>(null);
  const saveOptions = [
    { label: "All certificates in a single .pdf", value: "pdf" },
    { label: "All certificates in a .zip", value: "zip" },
  ];
  const [generateHistorical, setGenerateHistorical] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [pdfUrls, setPdfUrls] = React.useState<{ previewUrl: string; downloadUrl: string } | null>(null);

  React.useEffect(() => {
    if (!visible) {
      setCertificate(null);
      setAdditionalInterest([]);
      setDownloadOption('pdf');
      setSaveOption(null);
      setGenerateHistorical(false);
      setPdfUrls(null);
    }
  }, [visible]);

  React.useEffect(() => {
    if (!visible || !insuredDatabaseId) {
      setLoadingMaster(false);
      return;
    }
    setLoadingMaster(true);
    setError(null);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/master-certificates?insuredDatabaseId=${insuredDatabaseId}`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })
      .then(res => res.ok ? res : Promise.reject(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          const activeCerts = data.value.filter((cert: any) => cert.active !== false);
          setMasterCertificatesRaw(activeCerts);
          setMasterCertificateOptions(activeCerts.map((cert: any) => ({ label: cert.name, value: cert.databaseId })));
        } else {
          setMasterCertificatesRaw([]);
          setMasterCertificateOptions([]);
        }
      })
      .catch(() => setError('Failed to load master certificates'))
      .finally(() => setLoadingMaster(false));
  }, [visible, insuredDatabaseId]);

  React.useEffect(() => {
    if (!visible || !insuredDatabaseId) {
      setLoadingInterest(false);
      return;
    }
    setLoadingInterest(true);
    setInterestError(null);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetchAllAdditionalInterests(process.env.REACT_APP_COIPortal_Url ?? '', insuredDatabaseId, {
      'NCToken': ncToken,
      'Accept': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    })
      .then(items => {
        const active = items.filter((item: any) => item.active);
        setAdditionalInterestsRaw(active);
        setAdditionalInterestOptions(active.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId })));
      })
      .catch(() => setInterestError('Failed to load additional interests'))
      .finally(() => setLoadingInterest(false));
  }, [visible, insuredDatabaseId]);

  // Back to the first page, and no stale search, whenever the option list itself changes, so
  // neither can carry over from a previous certificate and hide the new list behind them.
  React.useEffect(() => {
    setAdditionalInterestFirst(0);
    setAdditionalInterestSearch('');
  }, [additionalInterestOptions]);

  // Search runs over the whole list before paging, never over the page. The MultiSelect's
  // own filtering only sees the options it is handed, which is one page, so leaving it to
  // do the work would report "no results" for a holder sitting on another page. Its search
  // box is still the one on screen — `onFilter` just hands the term over here.
  const matchedAdditionalInterestOptions = React.useMemo(() => {
    const term = additionalInterestSearch.trim().toLowerCase();
    if (!term) return additionalInterestOptions;
    return additionalInterestOptions.filter(o => (o.label ?? '').toLowerCase().includes(term));
  }, [additionalInterestOptions, additionalInterestSearch]);

  // Only the current page is handed to the MultiSelect, so "select all" is taken over below:
  // it has to cover every match rather than the visible page, which is the whole point for an
  // insured with a hundred of them. Left alone, the built-in handler would replace the
  // selection with just this page's rows.
  const pagedAdditionalInterestOptions = React.useMemo(
    () => matchedAdditionalInterestOptions.slice(additionalInterestFirst, additionalInterestFirst + AI_PAGE_SIZE),
    [matchedAdditionalInterestOptions, additionalInterestFirst]
  );
  // "Select all" means "all of what the search matched". With no search that is the whole
  // list, so the common case is unchanged; with one active it adds or removes just the
  // matches and leaves choices made under a different search term alone.
  const allAdditionalInterestsSelected =
    matchedAdditionalInterestOptions.length > 0 &&
    matchedAdditionalInterestOptions.every(o => additionalInterest.includes(o.value));

  const toggleAllAdditionalInterests = (checked: boolean) => {
    const matched = matchedAdditionalInterestOptions.map(o => o.value);
    setAdditionalInterest(current =>
      checked
        ? current.filter(v => !matched.includes(v))
        : Array.from(new Set([...current, ...matched]))
    );
  };

  // Filter additional interests to only those matching the selected master cert's policies.
  //
  // With no certificate chosen, or one that links no policies, there is no policy to match
  // on. The list is then narrowed to interests this user last changed, to put their own
  // recent work first. That preference is kept, but it can no longer empty the picker: AMS
  // does not return lastChangeUserId at all on this tenant, so the comparison was false for
  // every row and a certificate with no linked policies offered nothing at all, however many
  // interests the insured had. It looked exactly like the paging bug and was not one.
  React.useEffect(() => {
    const ncUserId = localStorage.getItem('NCUserId');
    const toOptions = (rows: any[]) =>
      rows.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId }));

    const withoutPolicyFilter = () => {
      const mine = ncUserId
        ? additionalInterestsRaw.filter((item: any) => item.lastChangeUserId === ncUserId)
        : [];
      return toOptions(mine.length > 0 ? mine : additionalInterestsRaw);
    };

    setAdditionalInterest([]);
    if (!certificate) {
      setAdditionalInterestOptions(withoutPolicyFilter());
      return;
    }
    const mc = masterCertificatesRaw.find((c: any) => c.databaseId === certificate);
    const mcPolicyIds: string[] = mc?.policies?.map((p: any) => p.databaseId as string) ?? [];
    if (mcPolicyIds.length === 0) {
      setAdditionalInterestOptions(withoutPolicyFilter());
      return;
    }
    const filtered = additionalInterestsRaw.filter((item: any) =>
      (item.policies ?? []).some((p: any) => mcPolicyIds.includes(p.databaseId))
    );
    setAdditionalInterestOptions(toOptions(filtered));
  }, [certificate, masterCertificatesRaw, additionalInterestsRaw]);

  const handleGenerateCertificate = async () => {
    if (!insuredDatabaseId || !certificate) {
      toastRef.current?.show({ severity: 'error', summary: 'Missing Data', detail: 'Please select a master certificate.', life: 2500 });
      return;
    }
    if (!additionalInterest.length) {
      toastRef.current?.show({ severity: 'error', summary: 'Missing Data', detail: 'Please select at least one additional interest.', life: 2500 });
      return;
    }
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    const payload = {
      insuredDatabaseId,
      certificateId: certificate,
      additionalInterestIds: additionalInterest,
      generateHistoricalCertificates: generateHistorical,
      DownloadOptions: generateHistorical ? '' : downloadOption.toUpperCase(),
      SaveOptions: '',
    };
    setGenerating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/generate-certificate`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      // A throttle is worth naming. The API caps requests per IP per minute, and generating
      // and downloading certificates one at a time for a large insured reaches it, at which
      // point every attempt fails until the minute rolls over. Reported as a flat failure it
      // looks like the feature is broken and there is nothing to do about it.
      if (res.status === 429) throw new Error(RATE_LIMITED);
      if (!res.ok) throw new Error('Failed to generate certificate');

      const json = await res.json().catch(() => null);

      if (json?.previewUrl || json?.downloadUrl) {
        // PDF/ZIP case — show preview dialog
        setPdfUrls({ previewUrl: json.previewUrl || '', downloadUrl: json.downloadUrl || '' });
      } else {
        // Historical JSON case — databaseIds returned
        const msg: string = json?.message || 'Certificate(s) generated successfully.';
        toastRef.current?.show({ severity: 'success', summary: 'Generated', detail: msg, life: 3000 });
        setTimeout(() => onHide(), 800);
      }
    } catch (err: any) {
      const throttled = err?.message === RATE_LIMITED;
      toastRef.current?.show({
        severity: throttled ? 'warn' : 'error',
        summary: throttled ? 'Too many requests' : 'Error',
        detail: throttled ? RATE_LIMITED : 'Failed to generate certificate.',
        life: throttled ? 6000 : 3000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleClosePdfPreview = () => {
    setPdfUrls(null);
    onHide();
  };

  return (
    <>
      <Dialog
        header={<span style={{ color: '#ba170b', fontWeight: 600 }}>Generate Certificate</span>}
        visible={visible && !pdfUrls}
        style={{ width: "500px", maxWidth: "98vw" }}
        onHide={onHide}
        modal
        className="addCertHolderDialog"
      >
        <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
                Certificate <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              </div>
              {error ? (
                <div style={{ color: 'red' }}>{error}</div>
              ) : (
                <Dropdown
                  value={certificate}
                  options={masterCertificateOptions}
                  onChange={e => setCertificate(e.value)}
                  placeholder={loadingMaster ? "Loading..." : "Select master certificate"}
                  style={{ width: '100%' }}
                  disabled={loadingMaster}
                  emptyMessage="No available options"
                />
              )}
            </div>
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
                Additional Interest <span style={{ color: 'red' }}>*</span>
              </div>
              {interestError ? (
                <div style={{ color: 'red' }}>{interestError}</div>
              ) : (
                <MultiSelect
                  value={additionalInterest}
                  options={pagedAdditionalInterestOptions}
                  onChange={e => setAdditionalInterest(e.value)}
                  placeholder={loadingInterest ? "Loading..." : "Select additional interest"}
                  style={{ width: '100%' }}
                  disabled={loadingInterest}
                  emptyMessage={
                    additionalInterestSearch.trim()
                      ? "No additional interests match that search"
                      : "No available options"
                  }
                  filter
                  filterPlaceholder="Search additional interests..."
                  onFilter={e => {
                    setAdditionalInterestSearch(e.filter ?? '');
                    setAdditionalInterestFirst(0);
                  }}
                  resetFilterOnHide
                  onHide={() => {
                    setAdditionalInterestSearch('');
                    setAdditionalInterestFirst(0);
                  }}
                  className="cert-holder-ms"
                  // Always summarise rather than listing what is selected. Naming them meant
                  // a selectedItemTemplate, because the component resolves labels from the
                  // options it holds and those are now one page, so a pick made on another
                  // page would come back nameless. But supplying that template makes it skip
                  // its own chip rendering and concatenate the raw output instead, which ran
                  // two holders together as one unbroken string. A count needs no lookup and
                  // no template, and it is what the field has to show at a hundred anyway.
                  // maxSelectedLabels is 0, not unset: the component tests it with isNotEmpty,
                  // for which 0 counts, so any selection at all falls to selectedItemsLabel.
                  maxSelectedLabels={0}
                  selectedItemsLabel="{0} selected"
                  selectAll={allAdditionalInterestsSelected}
                  onSelectAll={e => toggleAllAdditionalInterests(e.checked)}
                  panelFooterTemplate={
                    additionalInterestOptions.length > 0 ? (
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 8px', borderTop: '1px solid #e5e7eb' }}
                        // Keep the panel open when the pager is clicked — otherwise the blur closes it.
                        onMouseDown={e => e.preventDefault()}
                      >
                        <small style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {additionalInterestSearch.trim()
                            ? `${matchedAdditionalInterestOptions.length} of ${additionalInterestOptions.length} shown · ${additionalInterest.length} selected`
                            : `${additionalInterest.length} of ${additionalInterestOptions.length} selected`}
                        </small>
                        {matchedAdditionalInterestOptions.length > AI_PAGE_SIZE ? (
                          <Paginator
                            first={additionalInterestFirst}
                            rows={AI_PAGE_SIZE}
                            totalRecords={matchedAdditionalInterestOptions.length}
                            onPageChange={e => setAdditionalInterestFirst(e.first)}
                            template="PrevPageLink CurrentPageReport NextPageLink"
                            currentPageReportTemplate={`Page {currentPage} of ${Math.max(1, Math.ceil(matchedAdditionalInterestOptions.length / AI_PAGE_SIZE))}`}
                            style={{ padding: 0, background: 'transparent', border: 'none' }}
                          />
                        ) : null}
                      </div>
                    ) : null
                  }
                  appendTo={document.body}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                inputId="generateHistorical"
                checked={generateHistorical}
                onChange={e => setGenerateHistorical(e.checked ?? false)}
              />
              <label htmlFor="generateHistorical" style={{ color: '#0057A4', fontWeight: 500, marginLeft: 8 }}>
                Generate historical certificates for every additional interest
              </label>
            </div>
            {generateHistorical && (
              <div>
                <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>Save options</div>
                <Dropdown
                  value={saveOption}
                  options={saveOptions}
                  onChange={e => setSaveOption(e.value)}
                  placeholder="Please select save option"
                  style={{ width: '100%' }}
                  emptyMessage="No available options"
                />
              </div>
            )}
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>Download Options</div>
              <Dropdown
                value={downloadOption}
                options={downloadOptions}
                onChange={e => setDownloadOption(e.value)}
                placeholder="Please select download option"
                style={{ width: '100%' }}
                emptyMessage="No available options"
              />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={onHide}
            style={{ color: '#002A5C', fontWeight: 600 }}
          />
          <Button
            label={generating ? 'Generating...' : 'Generate Certificate'}
            icon={generating ? 'pi pi-spin pi-spinner' : undefined}
            className="p-button-primary"
            onClick={handleGenerateCertificate}
            disabled={generating || !certificate || !additionalInterest.length}
            style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff', fontWeight: 600 }}
          />
        </div>
        <Toast ref={toastRef} position="top-center" />
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        header={<span style={{ color: '#ba170b', fontWeight: 600 }}>Certificate Preview</span>}
        visible={!!pdfUrls}
        style={{ width: '85vw', maxWidth: '1100px' }}
        onHide={handleClosePdfPreview}
        modal
      >
        {pdfUrls?.previewUrl && (
          <iframe
            src={pdfUrls.previewUrl}
            title="Certificate preview"
            style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          {pdfUrls?.downloadUrl && (
            <Button
              label="Download"
              icon="pi pi-download"
              className="p-button-primary"
              onClick={() => window.open(pdfUrls.downloadUrl, '_blank')}
              style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff', fontWeight: 600 }}
            />
          )}
          <Button
            label="Close"
            className="p-button-text"
            onClick={handleClosePdfPreview}
            style={{ color: '#002A5C', fontWeight: 600 }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default GenrateCertificate;
